import Dropzone from "react-dropzone";
import SVG from "react-inlinesvg";
import IconButton from "src/components/IconButton";
import { cn } from "src/lib/cn";
import { SudoTooltip } from "src/sudokit/components/SudoTooltip";
import { IMAGE_TOO_LARGE_MESSAGE, IMAGE_UPLOAD_MAX_BYTES } from "./lib/constants";
import { handleReferenceImageDropRejected, REFERENCE_IMAGE_DROPZONE_ACCEPT } from "./lib/imageDropHandlers";

interface ReferenceImageDropzoneProps {
  file: File | null;
  previewUrl: string | null;
  onSelect: (file: File) => void;
  onError: (message: string) => void;
  onRemove: () => void;
  /** Square edge length in px. */
  size?: number;
}

export default function ReferenceImageDropzone({ file, previewUrl, onSelect, onError, onRemove, size = 60 }: ReferenceImageDropzoneProps) {
  return (
    <Dropzone
      accept={REFERENCE_IMAGE_DROPZONE_ACCEPT}
      maxFiles={1}
      onDrop={(acceptedFiles) => {
        const dropped = acceptedFiles[0];
        if (!dropped) return;
        if (dropped.size > IMAGE_UPLOAD_MAX_BYTES) {
          onError(IMAGE_TOO_LARGE_MESSAGE);
          return;
        }
        onSelect(dropped);
      }}
      onDropRejected={(fileRejections) => {
        const code = fileRejections[0]?.errors[0]?.code;
        if (code === "too-many-files") {
          onError("Please drop a single image.");
        } else {
          handleReferenceImageDropRejected(onError);
        }
      }}
    >
      {({ getRootProps, getInputProps, isDragActive }) => (
        <SudoTooltip
          text={file ? file.name : "Drop or click to add image"}
          position="top-start"
        >
          <div
            {...getRootProps()}
            style={{ width: size, height: size }}
            className={cn(
              "relative flex shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-xl border transition-colors duration-200 ease-out",
              file
                ? "border-solid border-app-grey-3"
                : "border-dashed border-app-grey-3 bg-[color-mix(in_srgb,var(--grey-4)_50%,transparent)] hover:border-[color:color-mix(in_srgb,var(--grey-0)_35%,transparent)]",
              isDragActive &&
                "border-[color:color-mix(in_srgb,var(--primary-cta-high-contrast)_45%,transparent)] bg-[color-mix(in_srgb,var(--primary-cta-high-contrast)_12%,transparent)]",
            )}
          >
            <input {...getInputProps()} />
            {file && previewUrl ? (
              <>
                <img
                  src={previewUrl}
                  alt={file.name}
                  className="h-full w-full object-cover"
                />
                <IconButton
                  src="/assets/close.svg"
                  size={20}
                  iconSize={10}
                  smartFill
                  ariaLabel="Remove reference image"
                  className="!absolute right-0.5 top-0.5 z-10"
                  wrapperClassName="!size-4 !bg-app-grey-4 hover:!bg-app-grey-3"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove();
                  }}
                />
              </>
            ) : (
              <SVG
                src="/assets/image.svg"
                width={24}
                height={24}
                className={cn("transition-transform duration-200 ease-out [&_path]:fill-app-grey-1", isDragActive && "scale-125 rotate-12 [&_path]:fill-app-primary-cta-high-contrast")}
                aria-hidden="true"
              />
            )}
          </div>
        </SudoTooltip>
      )}
    </Dropzone>
  );
}
